import { Loading, SearchInput } from "@alm/flow-ds/components";
import CardColumn from "components/CardColumn";
import DashboardLayout from "components/Layouts/DashboardLayout";
import { ListGroup, ListGroupItem } from "components/ListGroup";
import PageHeader from "components/PageHeader";
import { isAdmin } from "data/app/appSelectors";
import useCheckMountedStatus from "hooks/useCheckMountedStatus";
import usePagedData from "hooks/usePagedData";
import { useWorkflowList } from "hooks/useWorkflowList";
import api from "lib/api";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import UploadWorkflow from "./UploadWorkflow";
import WorkflowCard from "./WorkflowCard";
import { useMobileView } from "hooks/useMobileView";
import PaginationBar from "components/PaginationBar";

export interface Workflow {
	id: string;
	description: string;
	name: string;
}

export default function Workflows() {
	const { t } = useTranslation();

	const admin = useSelector(isAdmin);

	const { _isMounted } = useCheckMountedStatus();

	const [init, setInit] = useState(false);

	const [searchBox, setSearchBox] = useState("");
	const [searchValue, setSearchValue] = useState("");

	const [currentPage, setCurrentPage] = useState(1);
	const [total, setTotal] = useState<number | undefined>(0);
	const { paginationLimit } = useMobileView();

	// To track whether a new workflow has been added -
	// If a new workflow has been added we need to load
	// workflows again
	const [isWorkflowAdded, setIsWorkflowAdded] = useState(false);

	const onChangeEvent = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			if (event.target.value !== "") {
				setSearchBox(event.target.value);
			} else {
				setSearchBox(event.target.value);
				setSearchValue(event.target.value);
			}
		},
		[]
	);

	// Pagination for workflows list
	const { state, setData, ready, loading, searching, patchState } =
		usePagedData();

	const { limit } = state;

	const disableOnLoad = state.loading ? "disabled" : "";

	// Hook to handle workflow data changes
	const { deleteWorkflow } = useWorkflowList(state, patchState);

	const handleNewWorkflowAdded = useCallback(() => {
		setIsWorkflowAdded(true);
	}, []);

	/**
	 * Add new workflows to page when paged
	 */
	const pageData = useCallback(
		async (page: number) => {
			try {
				loading();
				const res = await api.workflow.getAllWorkflowDefinitions({
					limit: limit,
					offset: (page - 1) * limit,
					search: state.search,
				});
				setData(res);
				ready();
			} catch (e) {
				console.error(e);
			}
			setCurrentPage(page);
		},
		[setData, loading, ready, limit, state.search]
	);

	/**
	 * Load workflows on mount
	 */
	useEffect(() => {
		if (!init || isWorkflowAdded) {
			// Load workflows from API
			const getWorkflows = async () => {
				try {
					const results =
						await api.workflow.getAllWorkflowDefinitions({
							limit: 10,
							offset: 0,
							search: searchValue,
						});
					// Only update state when the component is mounted
					if (_isMounted.current) {
						setData(results);
						setTotal(results.totalCount);
						ready();
					}
				} catch (e) {
					console.error(e);
				}
			};

			getWorkflows();

			// Only update state when the component is mounted
			if (_isMounted.current) {
				setInit(true);
				setIsWorkflowAdded(false);
			}
		}
	}, [_isMounted, init, isWorkflowAdded, ready, setData, admin, searchValue]);

	/**
	 * Load new list of workflows when search term has been changed
	 */
	useEffect(() => {
		if (init) {
			searching();
			setCurrentPage(1);
			// Load workflows from API with search query
			api.workflow
				.getAllWorkflowDefinitions({
					limit: 10,
					offset: 0,
					search: searchValue,
				})
				.then(results => {
					setData(results);
					setTotal(results.totalCount);
					ready();
				})
				.catch(e => console.error(e));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchValue]);

	if (!init) return <Loading className="mt-4 elem__loader--md" />;

	return (
		<DashboardLayout>
			{admin ? (
				<div className="container">
					<div className="fixed-header-container">
						<PageHeader
							title={t("main.workflows.headings.workflows")}>
							<UploadWorkflow
								onNewWorkflowAdded={handleNewWorkflowAdded}
							/>
						</PageHeader>
						<div className="search-activities">
							<div className="search-bar">
								<SearchInput
									label={t("main.text.search")}
									onSearch={() => setSearchValue(searchBox)}
									onChange={onChangeEvent}
									value={searchBox}
								/>
							</div>
						</div>
					</div>
					{state.searching ? (
						<Loading className="mt-4 elem__loader--md" />
					) : state.results.length ? (
						<>
							<div
								className={`list-group-heading list-group-heading--position ${disableOnLoad}`}>
								<CardColumn width="sm">
									{t("main.workflows.headings.id")}
								</CardColumn>
								<CardColumn width="md">
									{t("main.workflows.headings.name")}
								</CardColumn>
								<CardColumn width="xl">
									{t("main.workflows.headings.description")}
								</CardColumn>
								<CardColumn className="workflow-card__menu"></CardColumn>
							</div>
							<ListGroup className="workflows-group">
								{state.results.map(
									(workflow: Workflow, index: number) => (
										<ListGroupItem
											className={`workflow-card ${disableOnLoad}`}
											key={`workflow-card__${index}`}>
											<WorkflowCard
												{...workflow}
												deleteWorkflow={deleteWorkflow}
											/>
										</ListGroupItem>
									)
								)}
							</ListGroup>
						</>
					) : (
						!state.loading && (
							<p className="text-center">
								{t(
									searchValue.trim() !== ""
										? "main.workflows.text.noSearch"
										: "main.workflows.text.noWorkflows"
								)}
							</p>
						)
					)}
					{total !== undefined &&
						total > limit &&
						!state.searching && (
							<PaginationBar
								currentPage={currentPage}
								limit={limit}
								total={total}
								paginationLimit={paginationLimit}
								onPageChange={page => {
									pageData(page);
								}}
								loading={state.loading}
							/>
						)}
				</div>
			) : (
				<Redirect to="/403" />
			)}
		</DashboardLayout>
	);
}
